import React, { useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { areaSearch, fetchPincodeInfo } from "@/lib/api";

const cleanName = (n) => (n || "").replace(/\s*\(.*?\)/g, "").trim();

// Resolves typed text (6-digit PIN or area name) to {pincode, area}; ambiguous names -> choices.
export function useAreaResolver(setPincode) {
  const [choices, setChoices] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = (r) => { setChoices([]); setError(""); setPincode(r.pincode, cleanName(r.name) || null); return r.pincode; };
  const resolve = async (text) => {
    const term = text.trim().replace(/\s+/g, " ");
    setChoices([]); setError("");
    if (!term) return null;
    setBusy(true);
    try {
      if (/^\d+$/.test(term)) {
        if (!/^[1-9]\d{5}$/.test(term)) { setError("PIN code 6 digits ka hona chahiye."); return null; }
        const info = await fetchPincodeInfo(term).catch((e) => { if (e?.response?.status !== 404) throw e; return null; });
        if (!info) { setError(`PIN ${term} verify nahi ho paya. Sahi PIN ya area ka naam daalein.`); return null; }
        return pick({ pincode: term, name: info.area });
      }
      if (term.length < 3) { setError("Area ka naam kam se kam 3 akshar ka daalein."); return null; }
      const res = (await areaSearch(term))?.results || [];
      const uniq = res.filter((r, i) => /^\d{6}$/.test(r.pincode) && res.findIndex((x) => x.pincode === r.pincode && x.name === r.name) === i);
      if (!uniq.length) { setError("Yeh area nahi mila. Sahi area naam ya 6-digit PIN daalein."); return null; }
      const pins = [...new Set(uniq.map((r) => r.pincode))];
      if (pins.length === 1) {
        if (!uniq[0].serviceable && !(await fetchPincodeInfo(pins[0]).catch(() => null))) { setError(`PIN ${pins[0]} verify nahi ho paya. Doosra area chunein.`); return null; }
        return pick(uniq[0]);
      }
      setChoices(uniq.slice(0, 12));
      return null;
    } catch {
      setError("Area check nahi ho paya. Internet check karke dobara try karein.");
      return null;
    } finally { setBusy(false); }
  };
  const choose = async (r) => {
    if (!r.serviceable && !(await fetchPincodeInfo(r.pincode).catch(() => null))) { setError(`PIN ${r.pincode} verify nahi ho paya. Doosra area chunein.`); return null; }
    return pick(r);
  };
  return { resolve, choose, choices, error, busy, clear: () => { setChoices([]); setError(""); } };
}

export function AreaChoices({ choices, onChoose, error }) {
  if (!choices.length && !error) return null;
  return (
    <div className="mt-2 px-1" data-testid="area-resolve-panel">
      {error && <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="area-resolve-error">{error}</div>}
      {choices.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white max-h-60 overflow-y-auto" data-testid="area-choices">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-3 pt-2">Ek se zyada area mile — sahi area chunein</div>
          {choices.map((r) => (
            <button type="button" key={`${r.name}-${r.pincode}`} onClick={() => onChoose(r)} className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-emerald-50 active:bg-emerald-100" data-testid={`area-choice-${r.pincode}`}>
              <MapPin className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{r.name} — {r.pincode}</div>
                <div className="text-[11px] text-slate-500 truncate">{[r.district, r.state].filter(Boolean).join(", ")}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AreaInput({ value, onChange, busy }) {
  return (
    <div className="flex-1 flex items-center gap-2 px-3 py-2.5 min-w-0">
      {busy ? <Loader2 className="w-4 h-4 text-emerald-700 animate-spin flex-shrink-0" /> : <MapPin className="w-4 h-4 text-emerald-700 flex-shrink-0" />}
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="PIN code ya area ka naam daalein"
        className="w-full bg-transparent outline-none text-sm sm:text-base placeholder:text-slate-400 min-w-0" data-testid="hero-area-input" />
    </div>
  );
}
